import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, List, message, Modal, Radio, Select } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';

import api from '../../services/api';
import { CadastroPage } from './CadastroPage';
import { listarConsultasV2 } from '../../services/cadastrosV2';
import { GridRazoesReprovacao } from '../Usuario/Cadastro/Consulta/RazaoReprovacao/gridRazoesReprovacao';

const { TextArea } = Input;

export function ConsultasV2() {
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [registrationTypes, setRegistrationTypes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => setPage(1), [debouncedSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listarConsultasV2(page, debouncedSearch);
      setItems(response?.data?.items || []);
      setTotal(response?.data?.count || 0);
    } catch (error) {
      setItems([]);
      setTotal(0);
      message.error(error?.response?.data?.detail || 'Não foi possível carregar as consultas.');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const loadOptions = async () => {
    const [supplierResponse, typeResponse] = await Promise.all([
      api.get('/Fornecedor/list'),
      api.get('/TipoCadastro/list'),
    ]);
    setSuppliers(supplierResponse?.data || []);
    setRegistrationTypes(typeResponse?.data || []);
  };

  const openNew = async () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      idTipoValidacao: 1,
      idTipoConsulta: 1,
      tiposCadastro: [],
      razoesForm: [],
    });
    setOpen(true);
    setSaving(true);
    try {
      await loadOptions();
    } catch (error) {
      message.error('Não foi possível carregar os dados auxiliares da consulta.');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = async item => {
    setOpen(true);
    setSaving(true);
    try {
      const [detailResponse] = await Promise.all([
        api.get('/Consulta/dados-cadastrais-consulta', { params: { idConsulta: item.id } }),
        loadOptions(),
      ]);
      const detail = detailResponse?.data;
      setEditing(detail);
      form.setFieldsValue({
        nome: detail.nome,
        idFornecedor: detail?.fornecedor?.id || detail.idFornecedor,
        link: detail.link,
        orientacao: detail.orientacao,
        idTipoValidacao: detail.idTipoValidacao || 1,
        idTipoConsulta: detail.idTipoConsulta || 1,
        tiposCadastro: (detail.tiposCadastroConsulta || []).map(
          type => type?.tipoCadastro?.id || type.idTipoCadastro
        ),
        razoesForm: detail.razoesReprovacaoConsulta || [],
      });
    } catch (error) {
      message.error('Não foi possível carregar os dados da consulta.');
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    setOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const save = async values => {
    setSaving(true);
    const payload = {
      id: editing?.id || 0,
      nome: values.nome.trim(),
      idFornecedor: values.idFornecedor,
      link: values.link,
      orientacao: values.orientacao,
      idTipoValidacao: values.idTipoValidacao,
      idTipoConsulta: values.idTipoConsulta,
      tiposCadastroConsulta: (values.tiposCadastro || []).map(idTipoCadastro => ({
        id: 0,
        idConsulta: editing?.id || 0,
        idTipoCadastro,
      })),
      razoesReprovacaoConsulta: values.razoesForm || [],
    };
    try {
      if (editing) await api.put('/Consulta', payload);
      else await api.post('/Consulta', payload);
      message.success(`Consulta ${editing ? 'atualizada' : 'cadastrada'} com sucesso.`);
      close();
      await load();
    } catch (error) {
      message.error(error?.response?.data?.detail || 'Não foi possível salvar a consulta.');
    } finally {
      setSaving(false);
    }
  };

  const remove = item => {
    Modal.confirm({
      title: `Excluir a consulta “${item.nome}”?`,
      content: 'A consulta só será excluída se não estiver vinculada a uma matriz.',
      okText: 'Excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await api.delete('/Consulta', { params: { id: item.id } });
          message.success('Consulta excluída com sucesso.');
          await load();
        } catch (error) {
          message.error(error?.response?.data?.detail || 'Não foi possível excluir a consulta.');
        }
      },
    });
  };

  return (
    <>
      <CadastroPage
        title="Consultas"
        newLabel="Nova consulta"
        search={search}
        onSearch={setSearch}
        loading={loading}
        items={items}
        total={total}
        page={page}
        onPageChange={setPage}
        onNew={openNew}
        renderItem={item => (
          <List.Item
            key={item.id}
            actions={[
              <Button key="edit" type="link" icon={<EditOutlined />} onClick={() => openEdit(item)}>Editar</Button>,
              <Button key="delete" type="link" danger icon={<DeleteOutlined />} onClick={() => remove(item)}>Excluir</Button>,
            ]}
          >
            <List.Item.Meta
              title={item.nome}
              description={`Fornecedor: ${item?.fornecedor?.nome || item?.fornecedor || 'Não informado'}`}
            />
          </List.Item>
        )}
      />

      <Modal
        open={open}
        title={editing ? 'Editar consulta' : 'Nova consulta'}
        footer={null}
        width={780}
        maskClosable={false}
        destroyOnHidden
        onCancel={close}
      >
        <Form form={form} layout="vertical" onFinish={save}>
          <Form.Item name="idTipoValidacao" label="Validação" rules={[{ required: true }]}>
            <Radio.Group options={[{ label: 'Interna', value: 1 }, { label: 'Externa', value: 2 }]} />
          </Form.Item>
          <Form.Item name="nome" label="Nome" rules={[{ required: true, message: 'Informe o nome.' }]}>
            <Input maxLength={150} />
          </Form.Item>
          <Form.Item name="idFornecedor" label="Fornecedor" rules={[{ required: true, message: 'Selecione o fornecedor.' }]}>
            <Select showSearch optionFilterProp="label" options={suppliers.map(item => ({ label: item.nome, value: item.id }))} />
          </Form.Item>
          <Form.Item name="link" label="Link">
            <Input maxLength={500} />
          </Form.Item>
          <Form.Item name="tiposCadastro" label="Tipos de cadastro">
            <Select mode="multiple" showSearch optionFilterProp="label" options={registrationTypes.map(item => ({ label: item.nome, value: item.id }))} />
          </Form.Item>
          <Form.Item name="orientacao" label="Orientações">
            <TextArea rows={3} maxLength={2000} showCount />
          </Form.Item>
          <Form.Item name="idTipoConsulta" label="Execução" rules={[{ required: true }]}>
            <Radio.Group options={[{ label: 'Automática', value: 1 }, { label: 'Manual', value: 2 }]} />
          </Form.Item>
          <Form.Item name="razoesForm" hidden />
          <GridRazoesReprovacao
            razoesReprovacaoConsulta={editing?.razoesReprovacaoConsulta || []}
            form={form}
            idConsulta={editing?.id || null}
          />
          <div className="cadastros-v2-modal-actions">
            <Button onClick={close}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={saving}>Salvar</Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}
